/**
 * Player Standings Component
 * Componente riutilizzabile per visualizzare classifiche individuali dei giocatori
 */

import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

interface PlayerStanding {
  position: number;
  team_name: string;
  matches_played: number;
  wins: number;
  losses: number;
  draws?: number;
  goals_for: number;
  goals_against: number;
  goal_difference: number;
  games_won: number;
  games_lost: number;
  game_difference: number;
  sets_won_with_7_games?: number;
  points: number;
}

@customElement('pm-player-standings')
export class PlayerStandingsComponent extends LitElement {
  @property({ type: Array }) standings: PlayerStanding[] = [];
  @property({ type: String }) title = 'Classifica Giocatori';
  @property({ type: Boolean }) showInfo = true;
  @property({ type: Boolean }) showLegend = true;
  @property({ type: Boolean }) compact = false;
  @property({ type: String, attribute: 'tournament-id' }) tournamentId = '';
  @property({ type: Boolean, attribute: 'auto-refresh' }) autoRefresh = false;
  @property({ type: Number, attribute: 'playoff-qualified-count' }) playoffQualifiedCount = 0;

  // Rimuovi Shadow DOM per usare gli stili globali del sito
  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Ascolta l'evento match-updated per auto-refresh
    if (this.autoRefresh && this.tournamentId) {
      document.addEventListener('match-updated', this.handleMatchUpdated);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('match-updated', this.handleMatchUpdated);
  }

  private handleMatchUpdated = async (event: Event) => {
    const customEvent = event as CustomEvent;
    
    if (!this.tournamentId) {
      return;
    }

    await this.loadStandings();
  };

  private async loadStandings() {
    try {
      const response = await fetch(`/simulations/api/tournaments/${this.tournamentId}/standings/`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.standings && Array.isArray(data.standings)) {
        this.standings = data.standings;
      }
    } catch (error) {
      console.error('[pm-player-standings] Errore nel caricamento della classifica:', error);
    }
  }

  render() {
    if (!this.standings || this.standings.length === 0) {
      return html`
        <div class="round-robin-section">
          <div class="section-header">
            <i class="fas fa-trophy me-2"></i>${this.title}
          </div>
          <div class="alert alert-info">
            <i class="fas fa-info-circle me-2"></i>
            Nessun dato disponibile. Completa alcuni match per vedere la classifica.
          </div>
        </div>
      `;
    }

    return html`
      <div class="round-robin-section">
        <div class="section-header">
          <i class="fas fa-trophy me-2"></i>${this.title}
        </div>
        
        ${this.showInfo ? html`
          <div class="alert alert-info mb-3">
            <i class="fas fa-info-circle me-2"></i>
            <strong>Classifica individuale:</strong> Ogni giocatore accumula punti indipendentemente dal compagno di coppia.
          </div>
        ` : ''}
        
        <div class="standings-table">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th class="position-col">#</th>
                <th class="team-col">Giocatore</th>
                <th class="text-center">PG</th>
                <th class="text-center">V</th>
                <th class="text-center">S</th>
                ${!this.compact ? html`
                  <th class="text-center">SV</th>
                  <th class="text-center">SP</th>
                ` : ''}
                <th class="text-center">DS</th>
                ${!this.compact ? html`
                  <th class="text-center">GV</th>
                  <th class="text-center">GP</th>
                ` : ''}
                <th class="text-center">DG</th>
                <th class="text-center"><strong>Pt</strong></th>
              </tr>
            </thead>
            <tbody>
              ${this.standings.map((player) => html`
                <tr class="${this.getRowClass(player.position)}">
                  <td class="position-col">
                    ${player.position}
                    ${this.getPositionIcon(player.position)}
                  </td>
                  <td class="team-col">
                    <strong>${player.team_name}</strong>
                  </td>
                  <td class="text-center">${player.matches_played}</td>
                  <td class="text-center text-success fw-bold">${player.wins}</td>
                  <td class="text-center text-danger">${player.losses}</td>
                  ${!this.compact ? html`
                    <td class="text-center">${player.goals_for}</td>
                    <td class="text-center">${player.goals_against}</td>
                  ` : ''}
                  <td class="text-center">
                    ${this.formatDifference(player.goal_difference)}
                  </td>
                  ${!this.compact ? html`
                    <td class="text-center">${player.games_won}</td>
                    <td class="text-center">${player.games_lost}</td>
                  ` : ''}
                  <td class="text-center">
                    ${this.formatDifference(player.game_difference)}
                  </td>
                  <td class="text-center fw-bold text-primary">${player.points}</td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
        
        ${this.showLegend ? html`
          <div class="alert alert-light mb-0 mt-3">
            <small class="text-muted">
              <strong><i class="fas fa-info-circle me-1"></i>Legenda:</strong> 
              PG = Partite Giocate • V = Vittorie • S = Sconfitte • 
              ${!this.compact ? 'SV = Set Vinti • SP = Set Persi • ' : ''}
              DS = Differenza Set • 
              ${!this.compact ? 'GV = Game Vinti • GP = Game Persi • ' : ''}
              DG = Differenza Game • 
              Pt = Punti (3 per vittoria, 1 per pareggio)
            </small>
          </div>
        ` : ''}
      </div>
    `;
  }

  private getRowClass(position: number): string {
    if (position === 1) return 'champion-row';
    if (position <= 3) return 'podium-row';
    return '';
  }

  private getPositionIcon(position: number) {
    // Se playoffQualifiedCount è impostato (Championship Knockout)
    if (this.playoffQualifiedCount > 0) {
      if (position === 1) {
        return html`<i class="fas fa-crown text-warning ms-1" title="Primo in Regular Season"></i>`;
      } else if (position <= this.playoffQualifiedCount) {
        return html`<i class="fas fa-star text-success ms-1" title="Qualificato ai Playoff"></i>`;
      }
      return '';
    }
    
    // Logica standard (Round Robin, etc.)
    if (position === 1) {
      return html`<i class="fas fa-crown text-warning ms-1"></i>`;
    } else if (position === 2) {
      return html`<i class="fas fa-medal text-secondary ms-1"></i>`;
    } else if (position === 3) {
      return html`<i class="fas fa-medal text-warning ms-1"></i>`;
    }
    return '';
  }

  private formatDifference(value: number) {
    if (value > 0) {
      return html`<span class="text-success">+${value}</span>`;
    } else if (value < 0) {
      return html`<span class="text-danger">${value}</span>`;
    } else {
      return html`<span class="text-muted">0</span>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-player-standings': PlayerStandingsComponent;
  }
}
